// 전자결재 타입 정의
import { Meta } from '@/types/legacyApi';
import type { Passport, SearchFields } from '@/types/api';

// --- 상태 ---

export type ApprovalState = {
  headerKey: number;
  myApprovalKey: number;
  myWriteKey: number;
  unRead: boolean;
};

export type WebApprovalState = {
  open: boolean;
  keyword: string;
};

// --- SNB 상태 뱃지 ---

/** status-badge 응답의 상태별 카운트 항목 (status: 'C' | 'R'). */
export type ApprovalAuthorStatusBadgeItem = {
  status: string;
  count: number;
};

/**
 * 작성자 본인 문서의 완료/반려 카운트 응답.
 * GET /api/v1/legacy-approval/author/me/status-badge → { data: ApprovalAuthorStatusBadge }.
 * 자가결재 제외는 서버가 처리하므로 FE 는 count 를 그대로 사용한다(PORTAL-12516).
 */
export type ApprovalAuthorStatusBadge = {
  completed: ApprovalAuthorStatusBadgeItem;
  rejected: ApprovalAuthorStatusBadgeItem;
  totalCount: number;
};

// --- 필터 ---

export type ApprovalTempFilter = {
  types: ApprovalType[];
  authorPassportIds: FilterPassport[];
  approverPassportIds: FilterPassport[];
  currentApproverPassportIds: FilterPassport[];
  coApproverPassportIds: FilterPassport[];
  finalApproverPassportIds: FilterPassport[];
};

export enum ApprovalFilterType {
  '즐겨찾기',
  '전체 문서',
  '내가 결재자인',
  '결재할 문서',
  '결재한 문서',
  '협조결재한 문서',
  '내가 작성자인',
  '진행중 문서',
  '임시저장 문서',
  '완료 문서',
  '반려 문서',
  '내가 참조자인 문서',
  '팀원의 작성 문서',
  '팀원의 협조결재 문서',
  '내 필터',
}

export type ApprovalFilter = {
  id: ApprovalFilterType | string;
  sortType: string;
  types: ApprovalType[];
  authorPassportIds: FilterPassport[];
  approverPassportIds: FilterPassport[];
  approverStatuses: string[];
  currentApproverPassportIds: FilterPassport[];
  coApproverPassportIds: FilterPassport[];
  coApproverStatuses: string[];
  statuses: FilterStatus[];
  referrerPassportIds: FilterPassport[];
  authorDepartmentIds: string[];
  approverDepartmentIds: string[];
  endAuthoredAt: PeriodStatus[];
  unreadPassportIds: FilterPassport[];
  isBookmark: boolean;
  finalApproverPassportIds: FilterPassport[];
  requireAllApprovers: boolean;
  requireAllCoApprovers: boolean;
  filterName?: string;
  keyword: string;
  searchFields: SearchFields;
  /** 제목 필터 독립 검색어 (BE titleKeyword 파라미터로 전달, 다른 검색어와 AND 조합) */
  titleKeyword?: string;
  /** 내용 필터 독립 검색어 (BE contentKeyword 파라미터로 전달, 다른 검색어와 AND 조합) */
  contentKeyword?: string;
};

export type PeopleFilterName =
  | 'authorPassportIds'
  | 'approverPassportIds'
  | 'currentApproverPassportIds'
  | 'coApproverPassportIds'
  | 'finalApproverPassportIds';

export type AllFilterName = 'requireAllApprovers' | 'requireAllCoApprovers';

export type StatusFilterName = 'endAuthoredAt' | 'statuses';

export type FilterPassport = {
  passportId: string;
  profileImageUrl: string;
  positionName: string;
  name: string;
  divisionName: string;
  departmentName: string;
  type?: number;
};

export type FilterStatus = {
  name: string;
  type: number;
  value: string;
  value2?: string;
};

export type PeriodStatus = {
  name: string;
  type: number;
  value: string;
  value2: string;
};

// --- 검색 파라미터 ---

export type TotalApprovalParams = {
  types?: string[];
  listSize?: number;
  currentPage?: number;
  sortType?: string;
  orderType?: 'desc' | 'asc';
  keyword?: string;
  authorPassportIds?: string[];
  approverPassportIds?: string[];
  approverStatuses?: string;
  currentApproverPassportIds?: string[];
  coApproverPassportIds?: string[];
  coApproverStatuses?: string;
  statuses?: string[];
  referrerPassportIds?: string[];
  authorCombinedDepartments?: string[];
  approverCombinedDepartments?: string[];
  startAuthoredAt?: string;
  endAuthoredAt?: string;
  unreadPassportIds?: string[];
  isBookmark?: boolean;
  finalApproverPassportIds?: string[];
  requireAllApprovers?: boolean;
  requireAllCoApprovers?: boolean;
  searchFields?: string;
  /** 고급 검색(query_string 문법): 통합검색창 입력을 그대로 전달 */
  query?: string;
  /** 제목 필드 전용 검색어 (일반 텍스트, query 와 AND 조합) */
  titleKeyword?: string;
  /** 내용 필드 전용 검색어 (일반 텍스트, query 와 AND 조합) */
  contentKeyword?: string;
  /** titleKeyword+contentKeyword 둘 다 있을 때의 결합 방식 (미전송 시 BE 는 OR) */
  keywordCondition?: 'and' | 'or';
  authorDepartmentIds?: string[];
  approverDepartmentIds?: string[];
};

export type ApprovalParams = {
  listSize?: number;
  currentPage?: number;
  sortType?: 'authoredAt';
  orderType?: 'desc' | 'asc';
  statuses?: string;
  isUnread?: boolean;
  approverType?: 1 | 2;
  myTurn?: boolean;
  keyword?: string;
};

// --- 엔티티 ---

export type ApprovalUserBase = {
  passportId: string;
  name: string;
  placeCode: string;
  placeName: string;
  departmentCode: string;
  departmentName: string;
  positionCode: string;
  positionName: string;
};

export type ApprovalAuthor = ApprovalUserBase & {
  employeeNumber: string;
  companyName: string;
  companyCode: string;
  profileImageUrl: string;
};

export type ApprovalApprover = ApprovalUserBase & {
  type: number;
  order: number;
  opinion: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

// --- 연관품의 ---

export type RelatedApprovalRelationType = 'parents' | 'children' | 'related';

export type RelatedApprovalItem = {
  docId: string;
  subject: string;
  // 문서 상태 코드(P/R/C/H/S/D)
  status?: string;
  author: { name: string };
  authoredAt: string;
};

/**
 * GET /api/v1/legacy-approval/{documentId} 응답 중 연관품의 부분.
 * 작성/편집 폼의 편집 모드 hydration 전용 (조회 화면은 v2 단건조회로 통합됨).
 */
export type ApprovalPortalRelationsData = {
  parents?: RelatedApprovalItem[];
  children?: RelatedApprovalItem[];
  related?: RelatedApprovalItem[];
};

/**
 * POST /api/v1/legacy-approval/{documentId}/summary 응답 data.
 * - `aiPipelineId !== null` → SSE 구독 대기 시작
 * - `aiPipelineId === null` → 본문 없음(skipped). SSE 종료, no-content 안내
 */
export type ApprovalAiSummaryRequestResponse = {
  aiPipelineId: string | null;
};

export type AddRelationDocument = {
  id: string;
  relationType: RelatedApprovalRelationType;
};

// --- 연관품의 변경이력 ---
// GET /api/v1/history/approval/relation/{docid} 응답 항목.

/**
 * 변경이력 액션 종류.
 *  - `create`         : 연결 품의 추가
 *  - `delete`         : 사용자가 연결 해제
 *  - `cascade_delete` : 원본 품의 삭제로 인한 자동 해제 ("다시 연결" 미노출)
 */
export type RelationHistoryAction = 'create' | 'delete' | 'cascade_delete';

/**
 * 변경이력 항목의 대상 품의 정보. status === 'D' 면 삭제된 품의.
 */
export type RelationHistoryRelatedDocument = {
  docId: string;
  subject: string;
  status?: string;
};

export type RelationHistoryItem = {
  id?: string;
  documentId?: string;
  relatedDocumentId?: string;
  relationType?: RelatedApprovalRelationType;
  action: RelationHistoryAction;
  actor?: { id?: string; name?: string };
  relatedDocument?: RelationHistoryRelatedDocument;
  /** ISO datetime 또는 'YYYY-MM-DD HH:mm:ss' 등. 파서가 둘 다 허용. */
  updatedAt?: string;
};

/**
 * 1초 미만 인접 항목을 묶어 만든 배치. 대표 시간(`updatedAt`)=배치 내 가장 이른 updatedAt.
 * action 은 배치 첫 항목 기준 (다른 action 은 같은 배치로 묶이지 않음).
 */
export type RelationHistoryBatch = {
  id: string;
  action: RelationHistoryAction;
  actor?: { id?: string; name?: string };
  updatedAt: string;
  items: RelationHistoryItem[];
};

export type AddRelationRequest = {
  documents: AddRelationDocument[];
};

export type DeleteRelationRequest = {
  relationDocumentIds: string[];
};

export type ApprovalLastOpinionBy = ApprovalUserBase & {
  type: number;
  order: number;
  status: string;
  userId: string;
  employeeNumber: string;
  profileImageUrl: string;
  opinion: string;
  approvedAt: string;
  createdAt: string;
  updatedAt: string;
};

export type Approval = {
  docId: string;
  isBookmark: boolean;
  type: {
    code: number;
    name: string;
    tag: string;
  };
  status: string;
  subject: string;
  author: ApprovalAuthor;
  currentApprover: ApprovalApprover;
  finalApprover: ApprovalApprover;
  lastOpinionBy?: ApprovalLastOpinionBy;
  opinionCount: number;
  commentCount: number;
  authoredAt: string;
  latestApprovedAt: string;
  createdAt: string;
  updatedAt: string;
  link: string;
  latestComment?: ApprovalComment;
};

export type ApprovalComment = {
  id: string;
  commentGroupId: string;
  content: string;
  createdBy: {
    passportId: string;
    activated: number;
    job: string;
    jobDescription: {
      summary: string;
      content: string;
      updatedAt: string;
    };
    detail: {
      employeeNumber: string;
      name: string;
      nickname: string;
      email: string;
      positionName: string;
      departmentId: string;
      departmentCode: string;
      departmentName: string;
      divisionCode: string;
      divisionName: string;
      companyCode: string;
      companyName: string;
      organizationCode: string;
      organizationFullName: string;
      organizationShortName: string;
      isLeader: boolean;
      profileImageUrl: string;
      phoneInside: string;
      phoneMobile: string;
      birthdayType: number;
      birthday: string;
      joinedAt: string;
    };
    /** @deprecated 응답에서 생략될 수 있으며, 직책은 detail 값으로 판정한다. */
    displaySetting?: {
      showTeamLeaderLabel: boolean;
      showConcurrentPositionLabel: boolean;
      showPartLeaderLabel: boolean;
      showActingTeamLeaderLabel: boolean;
    };
  };
  createdAt: string;
  updatedAt: string;
};

export type ApprovalType = {
  type: number;
  name: string;
  tag: string;
  sortNumber: number | null;
};

// --- 품의 양식 메타 (GET /api/v2/approval/type/{code} 응답) ---

/**
 * 양식 메타에 딸린 인물 항목 — 고정 참조인/고정 협조결재자/기본 참조인 공통 shape.
 *
 * 같은 v2 namespace 라도 문서 상세의 `ApprovalDetailApprover` 와 달리 인물 필드가 전부
 * plain string 이다 — dev 실응답 대조 기준(양식 24/93/214 등 데이터 있는 양식 전수).
 * BE 가 이 필드들을 다국어 객체로 바꾸면 참조인 칩에서 객체가 그대로 렌더돼 터지므로,
 * 그때는 `ApprovalI18nText` 를 열고 소비처에서 `pickI18nText` 로 좁혀야 한다.
 */
export type ApprovalTypeReferrer = {
  id: string;
  name: string;
  email: string;
  divisionName: string;
  departmentName: string;
  positionName: string;
  erpId: string;
};

/**
 * 품의 양식 메타 (본문 템플릿, 기본 보안레벨, 기본 참조인, 고정 협조결재자/참조인 포함).
 * `ApprovalType`은 품의 유형 목록 row에 이미 사용 중이라 상세 응답은 별도 이름으로 분리.
 *
 * 인물 배열 3종은 BE 가 요청자 소속 법인 기준으로 이미 좁혀서 flat 배열로 내려준다
 * (v1 의 companyCode 그룹 구조 아님). 퇴사자도 BE 에서 제외된다 — PORTAL-13902.
 */
export type ApprovalTypeDetail = {
  code: number;
  /** v2 다국어 객체. 표시 직전 `pickI18nText` 로 좁힐 것 */
  name: ApprovalI18nText | string | null;
  tag: ApprovalI18nText | string | null;
  description: ApprovalI18nText | string | null;
  subject: string | null;
  /** 1=전체, 5=부서, 7=결재라인, 9=비공개 */
  defaultSecurityLevel: number;
  isEnabled: boolean;
  isDisplay: boolean;
  /** TinyMCE 초기값 HTML */
  contentTemplate: string;
  /** 양식 가이드는 법인별로 갈려서 별도 조회(`useApprovalTypeGuide`)를 쓴다 — 여기 값은 미사용 */
  guide: string | null;
  /** 자동 적용 소비처 없음 — 고정 참조인(`fixedReferrers`)만 작성 화면에 반영한다 */
  defaultReferrers: ApprovalTypeReferrer[];
  /** 순차 결재자로 들어가는 고정 결재자 — 구버전 BE 응답엔 키 자체가 없을 수 있다 */
  fixedApprovers?: ApprovalTypeReferrer[];
  fixedCoApprovers: ApprovalTypeReferrer[];
  fixedReferrers: ApprovalTypeReferrer[];
  supportsDraftSource: boolean;
  supportsAiDraftSource: boolean;
  sortNumber: number | null;
  updatedAt: string;
  updatedBy: string | null;
};

/**
 * 법틀 계약 1건 — GET /type/{117|118}/draft-source 의 display.contracts 항목(PORTAL-14185).
 * contractId 외 전 필드는 법틀 응답에 따라 비어 올 수 있어 nullable.
 * accessLink 는 만료되는 링크라 저장하지 말고 조회 때마다 draft-source 로 다시 받을 것.
 */
export type ElawContract = {
  contractId: number;
  contractName?: string | null;
  viewCode?: string | null;
  /** 콤마 없는 숫자 문자열 — 표시 직전에만 포맷 */
  amount?: string | null;
  status?: string | null;
  statusName?: string | null;
  fileExtension?: string | null;
  completed?: boolean | null;
  creatorInfo?: string | null;
  opponentInfo?: string | null;
  accessLink?: string | null;
  startDate?: string | null;
  expireDate?: string | null;
  createdTime?: string | null;
  /** 신규·갱신 구분. 계약 ID 를 지정한 조회·저장 contractData 에만 옴(목록 조회는 null) */
  isNewContract?: boolean | null;
  /** 기한 없는 원문 다운로드 링크. BE 는 주지 않음 — 레거시 문서 저장 표에서 읽은 값만 */
  downloadUrl?: string | null;
};

/** GET /type/{117|118}/draft-source 응답 data */
export type ContractDraftSource = {
  code: number;
  attributes: {
    /** contractIds[] 를 지정해 부른 경우만 "48213,48219" 형태로 채워짐. 전건 조회는 null */
    contractIds: string | null;
  };
  display: {
    contracts: ElawContract[];
    /** partial=1 조회에서만. 요청 순서, 보는 사람 법틀 권한 기준 */
    contractResults?: ContractAccessResult[];
  };
};

/** partial=1 계약별 결과 — full 일 때만 contract(accessLink 포함), 권한 없음·법틀 장애면 null */
export type ContractAccessResult = {
  contractId: number;
  access: 'full' | 'forbidden' | 'unavailable';
  contract: ElawContract | null;
  errorCode?: string | null;
};

/**
 * 계약 품의(117·118) attributes — 저장(POST/PUT)·단건조회 공통.
 * 필드명은 contractIds — 다른 양식(99/100/177/179)의 sapDocumentNo 와 다름.
 * zsum 미전송, approvalLink 는 서버가 채우므로 보내도 무시됨.
 */
export type ContractApprovalAttributes = {
  content: string;
  /** 쉼표 결합 문자열, 최대 100자. 변환은 utils/approval/contractIds */
  contractIds?: string | null;
  /** 작성 시점 계약 표 HTML. 서버가 Oracle CL01 에 본문 앞에 붙여 저장 — 레거시 화면용 */
  contractContent?: string | null;
  /** 작성·수정 시점 계약 정보(accessLink 제외). 서버가 채우는 읽기 전용 — 요청에 넣으면 422. 레거시 문서는 null */
  contractData?: ElawContract[] | null;
};

/** SA 전표(99) 전표 상세(G/L) 행 — SAP DocumentItem 1건, draft-source display.vouchers[].items 항목 */
export type SaVoucherItem = {
  companyCode?: string | null;
  fiscalYear?: string | null;
  documentNumber?: string | null;
  lineItemNumber?: string | null;
  /** S/H */
  debitCreditCode?: string | null;
  /** 차변/대변 */
  debitCreditName?: string | null;
  accountCode?: string | null;
  accountName?: string | null;
  taxCode?: string | null;
  amount?: string | null;
  localAmount?: string | null;
  itemText?: string | null;
  partnerCode?: string | null;
  partnerName?: string | null;
  costCenterCode?: string | null;
  costCenterName?: string | null;
  paymentMethod?: string | null;
  /** 지급기산일 */
  baselineDate?: string | null;
};

/**
 * SA 전표(99) 전표 라인 1건 — draft-source display.vouchers 항목이자 단건조회 attributes.vouchers 항목.
 * 값은 SAP 원문(금액 콤마 포함, 날짜 yyyy.MM.dd)
 */
export type SaVoucherLine = {
  seq?: string | null;
  companyCode?: string | null;
  companyName?: string | null;
  fiscalYear?: string | null;
  documentNumber?: string | null;
  documentType?: string | null;
  documentDate?: string | null;
  postingDate?: string | null;
  currency?: string | null;
  amount?: string | null;
  localAmount?: string | null;
  headerText?: string | null;
  itemText?: string | null;
  text1?: string | null;
  text2?: string | null;
  text3?: string | null;
  text4?: string | null;
  /** SAP 전표 조회 화면 링크(레거시 Urllink1). 쿼리에 BUKRS·BELNR·GJAHR·sap-language */
  link?: string | null;
  /** 전표 상세(G/L) 행. 없으면 빈 배열, 이 필드 추가 전 저장 문서는 없음 */
  items?: SaVoucherItem[] | null;
};

/** GET /type/99/draft-source?sapDocumentNumber= 응답 data */
export type SaVoucherDraftSource = {
  code: number;
  attributes: {
    sapDocumentNo: string;
    zSum: string | null;
  };
  display: {
    vouchers: SaVoucherLine[];
  };
};

/** SAP 채권현황(185) 표 행 — SAP 원문 그대로(숫자도 대부분 서식된 문자열) */
export type SapBondStatusRow = Record<string, string | number | null>;

/** SAP 채권현황(185) 응답 원문 — ET_DATA 요약 + IT_DATA1~6 상세 */
export type SapBondStatusData = {
  ET_DATA?: Record<string, string | null> | null;
  IT_DATA1?: SapBondStatusRow[] | null;
  IT_DATA2?: SapBondStatusRow[] | null;
  IT_DATA3?: SapBondStatusRow[] | null;
  IT_DATA4?: SapBondStatusRow[] | null;
  IT_DATA5?: SapBondStatusRow[] | null;
  IT_DATA6?: SapBondStatusRow[] | null;
};

/** GET /type/185/draft-source 응답 data */
export type SapBondStatusDraftSource = {
  code: number;
  attributes: {
    year: number;
    month: number;
    brandCode: string;
    data: SapBondStatusData;
  };
  display: {
    source: SapBondStatusData;
  };
};

/** 할인권 발행(68) 결재 대상 할인권 — draft-source display.vouchers 항목 */
export type DiscountVoucherOption = {
  voucherNumber: string | null;
  label: string | null;
  brandCode: string | null;
  brandName: string | null;
  shopCode: string | null;
  shopName: string | null;
};

/** 할인권 발행(68) 할인 조건 행 — EAI 원문 그대로(숫자도 문자열) */
export type DiscountVoucherTerm = {
  voucherTypeName: string | null;
  description: string | null;
  minimumPurchaseAmount: string | null;
  maximumDiscountAmount: string | null;
  /** 'A' 면 discountUnit 이 %, 그 외 금액 */
  discountUnitType: string | null;
  discountUnit: string | null;
  quantity: string | null;
  /** 'A' 택가, 그 외 실판가 */
  priceBasis: string | null;
  /** 'ALL' 전체, 그 외 특정상품 */
  itemScope: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  /** 'A' 면 온/오프 통합 */
  channelTarget: string | null;
};

export type DiscountVoucherTargetSeason = {
  season: string | null;
  itemCount: string | null;
};

export type DiscountVoucherTargetItem = {
  season: string | null;
  partCode: string | null;
  name: string | null;
  tagPrice: string | null;
};

/** GET /type/68/draft-source 응답 data. voucherNumber 없이 부르면 terms 이하는 빈 배열 */
export type DiscountVoucherDraftSource = {
  code: number;
  attributes: {
    brandCode: string;
    brandName?: string | null;
    shopCode?: string | null;
    shopName?: string | null;
    voucherNumber?: string | null;
  };
  display: {
    vouchers: DiscountVoucherOption[];
    terms: DiscountVoucherTerm[];
    targetSeasons: DiscountVoucherTargetSeason[];
    targetItems: DiscountVoucherTargetItem[];
  };
};

/**
 * SA 전표(99) attributes — 저장(POST/PUT)·단건조회 공통.
 * zSum·vouchers 는 서버가 저장 직전 SAP 재조회로 덮어씀. vouchers 를 요청에 담으면 422.
 * 레거시 작성 문서는 vouchers 가 null 이고 content 에 전표 표 HTML 만 있음
 */
export type EasVoucherAttributes = {
  sapDocumentNo: string;
  zSum: string;
  content: string;
  approvalLink?: string | null;
  vouchers?: SaVoucherLine[] | null;
};

/**
 * 근태 취소 품의(226/227/232/237/238/239) attributes — 단건조회 응답 shape.
 * 저장(POST/PUT)은 approvalId 만 받는다 — subject/submittedAt/url 은 서버가 대상 문서에서 채우는
 * readOnly 필드라 요청에 담으면 422(스웨거 *CancellationAttributes). 변환은 utils/approval/cancelApproval
 */
export type CancelApprovalAttributes = {
  /** 취소 대상 원 품의 문서번호. 결재완료 후처리가 이 값으로 원 신청을 취소 */
  approvalId: string;
  subject?: string | null;
  /** YYYY-MM-DD */
  submittedAt?: string | null;
  /** 원 품의 조회 링크(포탈 뷰어) */
  url?: string | null;
};

/** POST /project-contract/draft 의 계약별 초안. content 는 평문(줄바꿈 \n) */
export type ProjectContractDraft = {
  contractId: number;
  content: string;
  /** 계약서 원본 presigned 링크 — 만료되므로 본문에 저장하지 않음 */
  file?: { url: string; mimeType?: string | null } | null;
};

/**
 * POST /project-contract/draft 응답 data. 스웨거 V2 는 200 스키마 미기재 —
 * v1 async 스키마 + 레거시 소비 코드 기준. 요청한 계약이 셋 중 어디에도 없으면 접근 불가 계약
 */
export type ProjectContractDraftResponse = {
  drafts?: ProjectContractDraft[] | null;
  pending?: number[] | null;
  failed?: number[] | null;
};

/** SSE draft_ready / draft_error 이벤트 payload. 레거시는 data.payload ?? data 로 읽음 */
export type ProjectContractDraftEvent = {
  type?: string;
  payload?: ProjectContractDraft | { contractId: number; message?: string };
  contractId?: number;
  content?: string;
  file?: ProjectContractDraft['file'];
};

/** 'common' 공통 가이드 / 'corporate' 법인 가이드. 미확인 값 대비로 string 을 열어둠 */
export type ApprovalTypeGuideSource =
  | 'common'
  | 'corporate'
  | (string & NonNullable<unknown>);

/**
 * 양식별 작성 가이드.
 * `guide` 는 마크다운 전환 예정이라 포맷 비의존 문자열로 유지 — 파싱은 렌더러 쪽에만 둘 것.
 * 미등록 양식은 200 + `guide: null` (source 는 'common').
 */
export type ApprovalTypeGuide = {
  code: number;
  companyCode: string;
  guide: string | null;
  source: ApprovalTypeGuideSource;
};

export type RecommendFilterResponse = {
  types: number[];
  authorPassports: Passport[];
  approverPassports: Passport[];
  currentApproverPassports: Passport[];
  coApproverPassports: Passport[];
  finalApproverPassports: Passport[];
  keywords: string[];
};

export type ApprovalList = {
  meta: Meta;
  data: Approval[];
};

// --- 즐겨찾기 필터 ---

export type FavoriteFilter = {
  name: string;
  description: string;
  params: FavoriteList;
};

export type FavoriteApprovalFilter = {
  meta: Meta;
  data: FavoriteFilterData[];
};

export type FavoriteList = Pick<
  ApprovalFilter,
  | 'endAuthoredAt'
  | 'types'
  | 'authorPassportIds'
  | 'approverPassportIds'
  | 'currentApproverPassportIds'
  | 'finalApproverPassportIds'
  | 'coApproverPassportIds'
  | 'statuses'
  | 'filterName'
  | 'requireAllApprovers'
  | 'requireAllCoApprovers'
  | 'keyword'
  | 'searchFields'
>;

export type FavoriteFilterData = {
  id: string;
  portalUserId: string;
  name: string;
  description: string;
  params: FavoriteList;
  createdAt: string;
  updatedAt: string;
};

// --- 첨부파일 ---

export type AttachmentUploadVariant =
  | 'default'
  | 'editor'
  | 'ai'
  | 'ai-verifier'
  | 'ai-ent-expense'
  | 'ai-report';

export type AttachmentUploadVariantConfig = {
  accept: string;
  allowedExtensions: Set<string>;
  multiple: boolean;
  maxSize: number;
  downloadType: 'public' | 'private';
  downloadExpirationSeconds?: number;
};

export type Attachment = {
  fileUuid?: string;
  fileId?: string;
  fileName: string;
  fileDesc?: string;
  fileSize: number;
  fileFormat: string;
  urlOrg: string;
  urlThOrg?: string;
  urlThS?: string;
  mimeType?: string;
  uploadDate: string;
};

/**
 * Attachment 의 안정 식별자 — 키/필터/삭제 비교용.
 * 신규 업로드는 fileUuid, 기존 유지는 fileId 우선. 둘 다 없으면 fileName fallback.
 */
export const getAttachmentKey = (a: Attachment): string =>
  a.fileUuid ?? a.fileId ?? a.fileName;

export type AttachmentUploadResult = {
  uuid: string;
  fileName: string;
  fileSizeInBytes: number;
  createdAt: string;
};

export type ApprovalAttachmentRef = {
  fileUuid: string;
};

export type AttachmentUploadProgress = {
  percent: number;
  loaded: number;
  total: number;
};

// --- 품의 작성/제출 (POST /api/v2/approval) ---
//
// 클라이언트 타입은 모두 optional — 임시저장 시 부분 페이로드를 허용하기 위함.
// SUBMIT 시 백엔드가 422 로 거절. 클라이언트 측 사전 검증은 호출부 책임.

/** 결재 진행 상태. DRAFT=임시저장 / SUBMIT=결재 진행 시작 */
export type ApprovalStatus = 'DRAFT' | 'SUBMIT';

/** 보안 레벨. 1=전체, 5=부서, 7=결재라인, 9=비공개 */
export type ApprovalSecurityLevel = 1 | 5 | 7 | 9;

export type CreateApprovalApprover = {
  passportId: string;
  order: number;
};

export type CreateApprovalReferrer = {
  passportId: string;
};

/**
 * 첨부 페이로드 항목. 두 형태 중 정확히 하나만 사용:
 * - 기존 유지: {fileId} (편집 모드, GET 응답에서 받은 fileId 그대로 전송)
 * - 신규 업로드: {fileUuid} (WPFile FileService 의 UUID)
 */
export type KeptAttachment = { fileId: string };
export type NewAttachment = { fileUuid: string };
export type CreateApprovalAttachment = KeptAttachment | NewAttachment;

/**
 * type 별 attributes oneOf — 호출부에서 type 코드에 맞춰 채워 넣는다.
 * - type=1  : DefaultApprovalAttributes — 빈 객체 또는 null
 * - type=32 : AnnualLeaveRequestAttributes — leaveType/leaveSubtype/startsAt/endsAt/...
 * - type=99 : EasVoucherAttributes — sapDocumentNo/zSum/approvalLink/content
 */
export type ApprovalAttributes = Record<string, unknown>;

export type CreateApprovalRequest = {
  type?: number;
  /** 제목. 백엔드 maxLength: 200 */
  subject?: string;
  /** 본문 HTML. 빈 본문은 null 로 보냄 */
  content?: string | null;
  status?: ApprovalStatus;
  /**
   * 상신자 의견. status=SUBMIT 일 때만 결재선에 저장되고 DRAFT 로는 보존되지 않음 —
   * 임시저장 후 재진입 시 안 내려오므로 입력은 상신 시점에만 받음 (PORTAL-13851).
   * 조회는 GET 응답의 authorOpinion.
   */
  opinion?: string | null;
  securityLevel?: ApprovalSecurityLevel | null;
  approvers?: CreateApprovalApprover[];
  coApprovers?: CreateApprovalApprover[] | null;
  referrers?: CreateApprovalReferrer[] | null;
  attributes?: ApprovalAttributes | null;
  attachments?: CreateApprovalAttachment[] | null;
  /** 첨부 이미지를 본문 하단에 표시할지 (레거시 image_display_yn). 미전송 시 백엔드 default(true). */
  showImagesBelowContent?: boolean;
  /** (구)마케팅 건별 진행(83) 전용 — 연결할 비용 건. null·생략이면 기존 연결 유지, 다른 유형에 보내면 422 */
  marketingCase?: {
    brandCode: string;
    season: string;
    costDocId: string;
    approvalDate: string;
  } | null;
};

/**
 * POST /api/v2/approval/{documentId} 응답.
 */
export type CreateApprovalResponse = unknown;
export type ApprovalDetailAttachment = {
  fileId: string;
  fileName: string;
  /** 원본 파일명(레거시 FILE_DESC). fileName 은 스토리지 키라 표시용으로 부적합 */
  fileDescription?: string | null;
  fileSize: number;
  fileFormat?: string;
  mimeType?: string;
  uploadDate?: string;
  url?: string;
  /** @deprecated 추정 키였음. 실제 응답은 `url`. 하위호환 fallback 으로만 유지. */
  urlOrg?: string;
  /** @deprecated 추정 키였음 — 별도 썸네일 URL 은 응답에 없고 url 을 공용. */
  urlThOrg?: string;
};

/**
 * v2 응답의 다국어 문자열. 일부 필드는 `{ ko, en, zh }` 객체로 옴. 표시 직전 좁혀야 함.
 * 실 좁힘은 `utils/approval/i18nText.ts` 의 `pickI18nText` 사용.
 */
export type ApprovalI18nText = Record<'ko' | 'en' | 'zh', string | null>;

/** GET /api/v2/approval/{documentId} 응답의 결재자 항목 */
export type ApprovalDetailApprover = {
  passportId: string;
  intranetId?: string;
  name?: ApprovalI18nText | string | null;
  positionName?: ApprovalI18nText | string | null;
  /** v2 응답에서 plain string 으로 확인됨. 다국어 객체로 변경되면 `ApprovalI18nText` 추가. */
  departmentName?: string;
  divisionName?: ApprovalI18nText | string | null;
  profileImageUrl?: string;
  type: 1 | 2; // 1=일반 결재자, 2=협조 결재자
  order: number; // 결재 순서. 협조 결재자는 일반 결재자 다음에 오지만, 협조 결재자끼리는 order 로 순서 지정.
  status?: string;
  opinion?: string | null;
  approvedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

/** GET 응답의 참조자 항목. 협조 결재자는 approvers[] 안에 들어오므로 본 배열은 참조자 전용. */
export type ApprovalDetailReferrer = {
  passportId: string;
  intranetId?: string;
  name?: ApprovalI18nText | string | null;
  positionName?: ApprovalI18nText | string | null;
  departmentName?: string;
  divisionName?: ApprovalI18nText | string | null;
  profileImageUrl?: string;
  createdAt?: string;
};

/**
 * GET /api/v2/approval/{documentId} 응답.
 *
 * 편집 모드 진입 시 폼 단순 본체 필드 + 기존 첨부를 복원하기 위해 사용.
 * 결재라인/참조자/연관품의/작성자 데이터는 서브컴포넌트가 각자 다른 경로로 로드/표시한다.
 */
export type ApprovalDetailResponse = {
  documentId: string;
  type: { code: number; name: unknown; tag: unknown };
  subject: string;
  content: string | null;
  /** 문서 진행 상태. S=임시저장 / P=진행 / R=반려 / C=완료 / H=보류 / D=삭제. */
  status: 'S' | 'P' | 'R' | 'C' | 'H' | 'D';
  /** 공개 레벨 (1=전체 / 5=소속부서 / 7=결재라인 / 9=상신자 본인). */
  security: ApprovalSecurityLevel | null;
  showImagesBelowContent: boolean;
  attributes: ApprovalAttributes | null;
  restrictReferrer?: boolean;
  attachments?: ApprovalDetailAttachment[];
  approvers?: ApprovalDetailApprover[];
  referrers?: ApprovalDetailReferrer[]; // 참조자
  additionalReferrers?: ApprovalDetailReferrer[]; // 추가 참조자
  readers?: ApprovalDetailReferrer[]; // 읽은 사람
};
